'use client';

import { PauseIcon, PlayIcon } from '@insta-club/components';
import React, { useEffect, useRef, useState } from 'react';
import useAudioStore from 'src/libs/state/stores/audio';
import RangeSlider from '../range-slider';

interface AudioPlayerProps {
    fileUrl: string;
    audioID: string;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({ fileUrl, audioID }) => {
    const [audioProgress, setAudioProgress] = useState(0);
    const [playbackSpeed, setPlaybackSpeed] = useState(1);
    const [isAudioPlaying, setIsAudioPlaying] = useState(false);
    const [isReady, setIsReady] = useState(false);
    const [duration, setDuration] = useState(0);

    const audioRef = useRef<HTMLAudioElement | null>(null);

    const currentAudioId = useAudioStore((s) => s.currentAudioId);
    const setCurrentAudio = useAudioStore((s) => s.setCurrentAudio);

    const handleAudioPlayPause = () => {
        if (!audioRef.current) return;

        if (isAudioPlaying) {
            audioRef.current.pause();
        } else {
            // Pause other audios
            const store = useAudioStore.getState();
            if (store.currentAudio && store.currentAudio !== audioRef.current) {
                store.currentAudio.pause();
            }

            // Play this audio immediately
            const playPromise = audioRef.current.play();

            if (playPromise !== undefined) {
                playPromise
                    .then(() => {
                        // Update store after successful play
                        setCurrentAudio(audioRef.current!, audioID);
                    })
                    .catch((error) => {
                        console.error('Error playing audio:', error);
                        setIsAudioPlaying(false);
                    });
            }
        }
    };

    const handleAudioTimeUpdate = () => {
        if (!audioRef.current) return;

        const { currentTime } = audioRef.current;

        if (duration > 0 && isFinite(duration)) {
            setAudioProgress((currentTime / duration) * 100);
        }
    };

    const handleAudioSeek = (value: number) => {
        if (!audioRef.current || !isReady) return;

        if (!duration || !isFinite(duration)) return;

        const newTime = (value / 100) * duration;

        if (!isFinite(newTime)) return;

        audioRef.current.currentTime = newTime;
        setAudioProgress(value);
    };

    useEffect(() => {
        // Only pause if another audio is playing (not this one)
        if (audioRef.current && currentAudioId && currentAudioId !== audioID && !audioRef.current.paused) {
            audioRef.current.pause();
        }
    }, [currentAudioId, audioID]);

    const onAudioReady = () => {
        if (!audioRef.current) return;

        const d = audioRef.current.duration;

        if (d && isFinite(d) && d > 0) {
            setDuration(d);
            setIsReady(true);
        }
    };

    const handleAudioError = (e: React.SyntheticEvent<HTMLAudioElement, Event>) => {
        console.error('Audio playback error:', e);
        setIsAudioPlaying(false);

        // Try to reload the audio element
        if (audioRef.current && !audioRef.current.error) {
            audioRef.current.load();
        }
    };

    useEffect(() => {
        if (!audioRef.current) return;

        const audio = audioRef.current;

        // Listen to play event to update state
        const handlePlay = () => {
            setIsAudioPlaying(true);
        };

        const handlePause = () => {
            setIsAudioPlaying(false);
        };

        audio.addEventListener('play', handlePlay);
        audio.addEventListener('pause', handlePause);

        return () => {
            audio.removeEventListener('play', handlePlay);
            audio.removeEventListener('pause', handlePause);
        };
    }, []);

    return (
        <div className='flex flex-row-reverse items-center gap-2 rounded-md'>
            <audio
                ref={audioRef}
                src={fileUrl}
                className='hidden'
                preload='metadata'
                playsInline
                onLoadedMetadata={onAudioReady}
                onCanPlay={onAudioReady}
                onTimeUpdate={handleAudioTimeUpdate}
                onError={handleAudioError}
                onEnded={() => {
                    setIsAudioPlaying(false);
                    setAudioProgress(0);
                }}
            />

            <div
                onClick={handleAudioPlayPause}
                className='bg-primary-500 flex h-7 w-7 cursor-pointer items-center justify-center rounded-full text-white'
            >
                {isAudioPlaying ? <PauseIcon width={12} height={12} /> : <PlayIcon width={12} height={12} />}
            </div>

            <RangeSlider
                value={audioProgress}
                onChange={(v) => handleAudioSeek(Number(v))}
                className='min-w-32 flex-1'
            />

            <div
                onClick={() => {
                    const newSpeed = playbackSpeed < 2 ? playbackSpeed + 0.5 : 1;
                    setPlaybackSpeed(newSpeed);

                    if (audioRef.current) {
                        audioRef.current.playbackRate = newSpeed;
                    }
                }}
                className='text-primary-500 flex h-7 w-7 cursor-pointer items-center justify-center rounded text-xs font-bold'
            >
                {playbackSpeed}x
            </div>
        </div>
    );
};

export default AudioPlayer;

